import { useState, useCallback, useEffect } from "react";
import { SavedCalculation } from "@/types/cvr";
import { useToast } from "@/hooks/use-toast";
import { fetchCVRCalculations } from "@/lib/supabase/cvr";
import { fetchEVMCalculations } from "@/lib/supabase/evm";
import { fetchEarlyWarnings } from "@/lib/supabase/early-warning"; // Added import
import { useSupabaseSubscription } from "./useSupabaseSubscription";
import { useAuth } from "@/context/AuthContext";

export function useCalculations(type: "cvr_calculations" | "evm_calculations" | "early_warning_register" = "cvr_calculations") {
  const [calculations, setCalculations] = useState<SavedCalculation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [activeCalculationId, setActiveCalculationId] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const loadCalculations = useCallback(async () => {
    if (!user) {
      setCalculations([]);
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      let data;
      
      switch (type) {
        case "evm_calculations":
          data = await fetchEVMCalculations();
          break;
        case "early_warning_register":
          data = await fetchEarlyWarnings();
          break;
        default:
          data = await fetchCVRCalculations();
      }
      
      console.log(`Fetched ${type} data:`, data);
      setCalculations(data);
    } catch (err) {
      console.error(`Error fetching ${type} calculations:`, err);
      const error = err instanceof Error ? err : new Error("Failed to fetch calculations");
      setError(error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, toast, type]);

  // Alias for loadCalculations to maintain API compatibility
  const refreshCalculations = loadCalculations;

  const addCalculation = useCallback((calculation: SavedCalculation) => {
    setCalculations(prev => [calculation, ...prev]);
  }, []);

  const updateCalculation = useCallback((id: string, updatedData: Partial<SavedCalculation>) => {
    setCalculations(prev =>
      prev.map(calc => calc.id === id ? { ...calc, ...updatedData } : calc)
    );
  }, []);

  const removeCalculation = useCallback((id: string) => {
    setCalculations(prev => prev.filter(calc => calc.id !== id));
  }, []);

  useEffect(() => {
    loadCalculations();
  }, [loadCalculations]);

  useSupabaseSubscription(type, loadCalculations);

  return {
    calculations,
    setCalculations,
    isLoading,
    error,
    loadCalculations,
    refreshCalculations,
    addCalculation,
    updateCalculation,
    removeCalculation,
    activeCalculationId,
    setActiveCalculationId,
  };
}